import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useReportError, extractErrorInfo } from '@features/support';
import { userService } from '../services/userService';
import type { User } from '../types/user.types';

export function useUsers(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  role?: string;
}) {
  // Cache key incluye page/search/role: cada combinación es una consulta real
  // distinta contra Aludra (paginación y búsqueda son server-side).
  return useQuery({
    queryKey: ['users', params?.page ?? 1, params?.search ?? '', params?.role ?? 'all'],
    queryFn: () => userService.getUsers(params),
    staleTime: 2 * 60 * 1000, // 2 minutes - data stays fresh
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    placeholderData: keepPreviousData, // Keep previous page/search results while the new key loads, so the input doesn't lose focus
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getUserById(id),
    enabled: !!id,
  });
}

/**
 * Traduce a lenguaje de usuario los errores del alta de usuario que llegan de
 * Aludra.
 *
 * Detección tolerante a propósito: Aludra no siempre escribe "already exists"
 * correctamente (se observó el typo "alredy exists"), así que no se depende de
 * esa ortografía. Se busca la raíz "exist" combinada con una mención a
 * usuario/userName, más los casos explícitos de duplicate/conflict.
 *
 * Si no reconoce el error, devuelve el mensaje original: es mejor un texto
 * técnico que uno genérico que oculte la causa real.
 */
function mensajeAmigableDeAltaDeUsuario(mensajeApi: string): string {
  const lower = (mensajeApi || '').toLowerCase();
  if (!lower) return 'No se pudo crear el usuario. Por favor, intenta de nuevo.';

  const mencionaExistente = /exist/.test(lower) || lower.includes('ya existe');
  const mencionaUsuario = /\buser(name)?\b/.test(lower) || lower.includes('usuario');
  const esDuplicado =
    (mencionaExistente && mencionaUsuario) ||
    lower.includes('duplicate') ||
    lower.includes('conflict');

  if (esDuplicado) {
    return 'Ya existe un usuario con este correo electrónico. Verifica los datos e intenta con otro correo.';
  }
  return mensajeApi;
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  // Asistente de soporte: explica el fallo del alta en vez del error crudo.
  const reportError = useReportError();

  return useMutation({
    mutationFn: (user: Omit<Partial<User>, 'companies'> & {
      email: string;
      idNumber: string;
      firstName: string;
      lastName: string;
      roles: string[];
      branchOffices: string[];
      companies: string[];
    }) => userService.createUser(user),
    // Simple: la API responde OK → se relanza la búsqueda del listado
    // (invalidate = refetch inmediato de la query activa, sin esperas
    // artificiales ni reintentos). Lo que traiga ese refetch es lo que se
    // muestra — nada se arma del lado del cliente.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
      queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
      // ['user', id] (singular, ficha individual de GET /users/:id) es una
      // query aparte de ['users', ...] (listado/búsqueda) — invalidar solo
      // esta última nunca refrescaba la ficha individual que usa el modal de
      // editar, mostrando datos obsoletos hasta un refresh completo de página.
      queryClient.invalidateQueries({ queryKey: ['user'], exact: false });
    },
    onError: (error: any) => {
      const info = extractErrorInfo(error);
      reportError({
        title: 'Error al crear usuario',
        screen: 'crear usuario',
        ...info,
        // El mensaje amigable va DESPUÉS del spread para pisar el de `info`,
        // que es el texto crudo de Aludra (en inglés y con el `UserName`
        // pegado). Antes esta traducción vivía en el `catch` de
        // CreateUserModal junto a un `Swal.fire` propio, así que el usuario
        // veía DOS diálogos de error apilados sobre el formulario: éste con el
        // texto crudo y el Swal con el traducido. `SupportErrorProvider` es el
        // modal de error único del portal; el Swal era el camino anterior que
        // quedó sin retirar.
        message: mensajeAmigableDeAltaDeUsuario(info.message),
      });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: {
      id: string;
      data: Omit<Partial<User>, 'companies'> & {
        email: string;
        idNumber: string;
        firstName: string;
        lastName: string;
        roles: string[];
        branchOffices: string[];
        companies: string[];
        password?: string;
      }
    }) =>
      userService.updateUser(id, data),
    onSuccess: async (_data, variables) => {
      // Wait for Aludra to index the updated user
      await new Promise(resolve => setTimeout(resolve, 1500));
      // Invalidate all user queries (including ['users', 'all'])
      queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
      // ['user', id] es una query aparte de ['users', ...] — sin esto, el
      // modal de editar (useUser) seguía sirviendo la ficha en caché de
      // antes de guardar (dentro del staleTime global de 1 min), mostrando
      // como si las compañías/sucursales recién agregadas no se hubieran
      // guardado hasta refrescar la página entera.
      queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: (_data, id) => {
      // Invalidate all user queries (including ['users', 'all'])
      queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}

export function useReactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.reactivateUser(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}

export function useResendInvitation() {
  return useMutation({
    mutationFn: (email: string) => userService.resendInvitation(email),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => userService.getRoles(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useAssignableCompanies() {
  return useQuery({
    queryKey: ['users', 'assignable-companies'],
    queryFn: () => userService.getAssignableCompanies(),
    staleTime: 60 * 1000, // 1 minuto — es una consulta fresca a Aludra, no se cachea agresivamente
  });
}

export function useBranchOffices() {
  return useQuery({
    queryKey: ['branch-offices'],
    queryFn: () => userService.getBranchOffices(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
